import type { ImageSegmenter } from '@mediapipe/tasks-vision';
import { fetchPreviewBlob } from '../../../project-essential/services/api';

/**
 * Cuts the plain studio backdrop out of an agent's portal photo so the
 * Accelerator title card can let the head rise above the ring, as in the
 * accelerator videos.
 *
 * The photo comes through the Project Essentials preview proxy (portal
 * images carry no CORS headers, and a cross-origin image would taint the
 * canvas). The backdrop is flood-filled from the top, left and right edges
 * only — never the bottom, where a white shirt meets the crop. A white collar
 * still touches a white backdrop at the neck, so MediaPipe's selfie
 * segmenter marks the pixels that are surely the person and the fill may not
 * enter them; the colour fill keeps the crisp hair edge the soft ML mask lacks.
 * Without the segmenter (offline CDN) the colour fill runs alone. Returns null
 * when the photo does not have a flat backdrop; callers then show the plain
 * round photo.
 */
export interface AgentCutout {
  /** Object URL of a transparent PNG. */
  url: string;
  /** width / height of the PNG. */
  aspect: number;
  /** Subject bounding box as fractions of the PNG (0–1). */
  top: number;
  left: number;
  right: number;
}

const MAX_SIDE = 900;
/** RGB distance below which a pixel counts as backdrop. */
const THRESHOLD = 40;
/** Pixels between THRESHOLD and THRESHOLD + FEATHER fade out at the edge. */
const FEATHER = 36;

/** Segmenter confidence above which a pixel is the person and never backdrop. */
const PERSON_CONFIDENCE = 0.9;
const TASKS_VISION_WASM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const SELFIE_SEGMENTER_MODEL =
  'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite';

const cache = new Map<string, Promise<AgentCutout | null>>();
let segmenterPromise: Promise<ImageSegmenter> | null = null;

/** Person mask at canvas resolution (1 = protected), or null when the segmenter is unavailable. */
async function personMask(canvas: HTMLCanvasElement): Promise<Uint8Array | null> {
  try {
    segmenterPromise ??= import('@mediapipe/tasks-vision').then(async ({ FilesetResolver, ImageSegmenter }) =>
      ImageSegmenter.createFromOptions(await FilesetResolver.forVisionTasks(TASKS_VISION_WASM), {
        baseOptions: { modelAssetPath: SELFIE_SEGMENTER_MODEL, delegate: 'CPU' },
        runningMode: 'IMAGE',
        outputConfidenceMasks: true,
        outputCategoryMask: false,
      }),
    );
    const segmenter = await segmenterPromise;
    const result = segmenter.segment(canvas);
    const mask = result.confidenceMasks?.[0];
    if (!mask) {
      result.close();
      return null;
    }
    const confidence = mask.getAsFloat32Array();
    const { width, height } = canvas;
    const protectedPixels = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
      const my = Math.min(mask.height - 1, Math.floor((y * mask.height) / height));
      for (let x = 0; x < width; x += 1) {
        const mx = Math.min(mask.width - 1, Math.floor((x * mask.width) / width));
        if (confidence[my * mask.width + mx] > PERSON_CONFIDENCE) protectedPixels[y * width + x] = 1;
      }
    }
    result.close();
    return protectedPixels;
  } catch (error) {
    console.warn('[accelerator] person segmenter unavailable', error);
    return null;
  }
}

export function loadAgentCutout(photoUrl: string): Promise<AgentCutout | null> {
  let pending = cache.get(photoUrl);
  if (!pending) {
    pending = buildCutout(photoUrl).catch((error) => {
      console.warn('[accelerator] photo cutout failed', error);
      return null;
    });
    cache.set(photoUrl, pending);
  }
  return pending;
}

async function buildCutout(photoUrl: string): Promise<AgentCutout | null> {
  const { objectUrl } = await fetchPreviewBlob(photoUrl);
  try {
    const image = await loadImage(objectUrl);
    const scale = Math.min(1, MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    if (!removeBackdrop(imageData, await personMask(canvas))) return null;
    ctx.putImageData(imageData, 0, 0);

    const box = subjectBox(imageData);
    if (!box) return null;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return {
      url: URL.createObjectURL(blob),
      aspect: width / height,
      top: box.top / height,
      left: box.left / width,
      right: box.right / width,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Photo failed to decode'));
    image.src = src;
  });
}

/** Clears the backdrop in place. False when the photo has no flat backdrop to remove. */
function removeBackdrop({ data, width, height }: ImageData, person: Uint8Array | null): boolean {
  // Backdrop colour = average of the two top corners; they must agree.
  const sample = (x: number, y: number) => {
    const i = (y * width + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  };
  const a = sample(1, 1);
  const b = sample(width - 2, 1);
  if (Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) > THRESHOLD) return false;
  const bg = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];

  const distance = new Float32Array(width * height);
  for (let p = 0; p < width * height; p += 1) {
    const i = p * 4;
    distance[p] = Math.hypot(data[i] - bg[0], data[i + 1] - bg[1], data[i + 2] - bg[2]);
  }

  // Flood fill the backdrop from the top, left and right edges.
  const backdrop = new Uint8Array(width * height);
  const stack: number[] = [];
  const seed = (p: number) => {
    if (!backdrop[p] && distance[p] < THRESHOLD && !person?.[p]) {
      backdrop[p] = 1;
      stack.push(p);
    }
  };
  for (let x = 0; x < width; x += 1) seed(x);
  for (let y = 0; y < height; y += 1) {
    seed(y * width);
    seed(y * width + width - 1);
  }
  while (stack.length) {
    const p = stack.pop()!;
    const x = p % width;
    if (x > 0) seed(p - 1);
    if (x < width - 1) seed(p + 1);
    if (p >= width) seed(p - width);
    if (p < width * (height - 1)) seed(p + width);
  }

  let cleared = 0;
  for (let p = 0; p < width * height; p += 1) if (backdrop[p]) cleared += 1;
  const share = cleared / (width * height);
  // Too little cleared: not a studio backdrop. Too much: we ate the agent.
  if (share < 0.08 || share > 0.85) return false;

  for (let p = 0; p < width * height; p += 1) {
    if (backdrop[p]) {
      data[p * 4 + 3] = 0;
      continue;
    }
    // Soften the outline: subject pixels touching the backdrop fade by colour distance.
    const x = p % width;
    const touches =
      (x > 0 && backdrop[p - 1]) ||
      (x < width - 1 && backdrop[p + 1]) ||
      (p >= width && backdrop[p - width]) ||
      (p < width * (height - 1) && backdrop[p + width]);
    if (touches) {
      const alpha = Math.min(1, Math.max(0, (distance[p] - THRESHOLD * 0.5) / FEATHER));
      data[p * 4 + 3] = Math.round(alpha * 255);
    }
  }
  return true;
}

function subjectBox({ data, width, height }: ImageData): { top: number; left: number; right: number } | null {
  let top = -1;
  let left = width;
  let right = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 128) {
        if (top < 0) top = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  return top < 0 ? null : { top, left, right: right + 1 };
}
